import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  LinearProgress,
} from '@mui/material'
import { CancelOutlined } from '@mui/icons-material'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import BrokenImageIcon from '@mui/icons-material/BrokenImage'
import ReplayIcon from '@mui/icons-material/Replay'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import LayersIcon from '@mui/icons-material/Layers'
import axios from 'axios'
import { DxfViewer } from 'dxf-viewer'

// ─── Constants ────────────────────────────────────────────────────────────────

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

// ─── Viewer state machine ──────────────────────────────────────────────────────

const VS = { IDLE: 'idle', LOADING: 'loading', READY: 'ready', ERROR: 'error' }

function viewerReducer(state, action) {
  switch (action.type) {
    case 'LOAD_START':
      return { status: VS.LOADING, error: null, zoom: null }
    case 'LOAD_SUCCESS':
      return { status: VS.READY, error: null, zoom: 100 }
    case 'LOAD_ERROR':
      return { status: VS.ERROR, error: action.payload, zoom: null }
    case 'SET_ZOOM':
      return { ...state, zoom: action.payload }
    case 'RESET':
      return { status: VS.IDLE, error: null, zoom: null }
    default:
      return state
  }
}

// ─── Error Boundary ───────────────────────────────────────────────────────────

class CadErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { caught: false }
  }
  static getDerivedStateFromError() {
    return { caught: true }
  }
  componentDidCatch(err) {
    console.error('CadPreview boundary caught:', err)
  }
  render() {
    if (!this.state.caught) return this.props.children
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a2e',
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 56, color: '#555', mb: 2 }} />
        <Typography sx={{ color: '#888', fontSize: 14, textAlign: 'center' }}>
          WebGL context lost. Please close and reopen the preview.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 2, color: '#7c83fd', borderColor: '#7c83fd' }}
          onClick={this.props.onClose}
        >
          Close
        </Button>
      </Box>
    )
  }
}

// ─── Toolbar primitives ───────────────────────────────────────────────────────

const ToolBtn = ({ title, onClick, disabled, active, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={
          active
            ? { color: '#7c83fd', backgroundColor: 'rgba(124,131,253,0.12)', borderRadius: 1 }
            : { borderRadius: 1 }
        }
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
)

const ToolDivider = () => (
  <Box sx={{ width: '1px', height: 20, backgroundColor: '#e0e0e0', mx: 0.5, flexShrink: 0 }} />
)

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * CadPreview
 *
 * Full-featured DXF viewer dialog.
 *
 * Props:
 *   open        {boolean}   – whether the dialog is open
 *   onClose     {function}  – called when the user closes the dialog
 *   fileKey     {string}    – S3 object key for DXF, e.g. "drawings/plan.dxf"
 *   user        {object}    – auth user object with `token`
 *   folder      {string}    – S3 folder prefix, default "files"
 */
const CadPreview = ({ open, onClose, fileKey, user, folder = 'files' }) => {
  // ── Refs ───────────────────────────────────────────────────────────────────
  const containerRef = useRef(null)
  const viewerRef = useRef(null)
  const blobUrlRef = useRef(null)
  const boundsRef = useRef(null)
  const paperRef = useRef(null)
  const retryCount = useRef(0)

  // ── Viewer state ───────────────────────────────────────────────────────────
  const [vState, dispatch] = useReducer(viewerReducer, { status: VS.IDLE, error: null, zoom: null })
  const [loadingPercentage, setLoadingPercentage] = useState(0)
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  // ── Feature states ─────────────────────────────────────────────────────────
  const [layers, setLayers] = useState([])
  const [layerVisibility, setLayerVisibility] = useState({})
  const [showLayerPanel, setShowLayerPanel] = useState(false)
  const [isDarkBg, setIsDarkBg] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const fileName = useMemo(() => fileKey?.split('/').pop() ?? '', [fileKey])
  const isReady = vState.status === VS.READY
  const isLoading = vState.status === VS.LOADING
  const hasError = vState.status === VS.ERROR

  // ── Derived: are all layers visible? ──────────────────────────────────────
  const allLayersVisible = useMemo(
    () => layers.length > 0 && layers.every((l) => layerVisibility[l.name] !== false),
    [layers, layerVisibility],
  )

  // ── Cleanup ────────────────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (viewerRef.current) {
      try { viewerRef.current.Destroy() } catch (_) { }
      viewerRef.current = null
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    boundsRef.current = null
  }, [])

  // ── Wait for container ────────────────────────────────────────────────────
  const waitForContainer = useCallback(
    (ref) =>
      new Promise((resolve, reject) => {
        const TIMEOUT_MS = 8000
        const POLL_MS = 30
        const deadline = Date.now() + TIMEOUT_MS
        let ro = null

        const tryAttach = () => {
          const el = ref.current
          if (!el) {
            if (Date.now() > deadline) {
              reject(new Error('Container not mounted: element never appeared in DOM'))
              return
            }
            setTimeout(tryAttach, POLL_MS)
            return
          }
          if (el.clientWidth > 0) { resolve(); return }
          ro = new ResizeObserver((entries) => {
            if (entries[0].contentRect.width > 0) { ro.disconnect(); resolve() }
          })
          ro.observe(el)
          setTimeout(() => {
            ro?.disconnect()
            reject(new Error('Container did not become visible in time'))
          }, deadline - Date.now())
        }

        tryAttach()
      }),
    [],
  )

  // ── Load effect ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !fileKey) return

    const abort = new AbortController()
    let cancelled = false

    const load = async () => {
      dispatch({ type: 'LOAD_START' })
      setLayers([])
      setLayerVisibility({})
      cleanup()

      try {
        await waitForContainer(containerRef)
        if (cancelled) return

        const { data: dxfText } = await axios.get(
          `${NODE_BASE_URL}/data-entry/file/cad-dxf/${encodeURIComponent(fileKey)}?folder=${folder}`,
          {
            headers: { Authorization: `Bearer ${user?.token}` },
            signal: abort.signal,
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                setLoadingPercentage(percentCompleted)
              }
            }
          },
        )
        if (cancelled) return

        const blob = new Blob([dxfText], { type: 'text/plain' })
        blobUrlRef.current = URL.createObjectURL(blob)

        const viewer = new DxfViewer(containerRef.current, {
          autoResize: true,
          blackWhiteInversion: true,
          colorCorrection: true,
        })
        viewerRef.current = viewer

        await viewer.Load({
          url: blobUrlRef.current,
          fonts: [],
          progressCbk: null,
          workerFactory: null,
        })
        if (cancelled) { cleanup(); return }

        const bounds = viewer.GetBounds?.()
        if (bounds) {
          viewer.FitView(bounds.minX, bounds.maxX, bounds.minY, bounds.maxY)
          boundsRef.current = bounds
        }

        try {
          const raw = viewer.GetLayers?.()
          let layerList = []
          if (raw) {
            if (typeof raw[Symbol.iterator] === 'function') {
              layerList = Array.from(raw).map((l) => ({
                name: l.name ?? String(l),
                visible: l.visible ?? true,
                color:
                  typeof l.color === 'number'
                    ? '#' + (l.color & 0xffffff).toString(16).padStart(6, '0')
                    : (l.color ?? '#7c83fd'),
              }))
            } else {
              layerList = Object.entries(raw).map(([name, data]) => ({
                name,
                visible: data.visible ?? true,
                color:
                  typeof data.color === 'number'
                    ? '#' + (data.color & 0xffffff).toString(16).padStart(6, '0')
                    : (data.color ?? '#7c83fd'),
              }))
            }
          }
          const vis = {}
          layerList.forEach((l) => { vis[l.name] = l.visible })
          setLayers(layerList)
          setLayerVisibility(vis)
        } catch (_) { }

        dispatch({ type: 'LOAD_SUCCESS' })
      } catch (err) {
        if (cancelled || axios.isCancel(err)) return
        const payloadErr = err?.response?.data?.error || err.message || 'Failed to load CAD preview'
        dispatch({ type: 'LOAD_ERROR', payload: payloadErr })
        setLoadingPercentage(0)
        cleanup()
      }
    }

    load()

    return () => {
      cancelled = true
      abort.abort()
      cleanup()
      dispatch({ type: 'RESET' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileKey, folder, user, retryCount.current, cleanup, waitForContainer])

  // ── Background colour ──────────────────────────────────────────────────────
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || !isReady) return
    try {
      const color = isDarkBg ? 0x1a1a2e : 0xf0f0f0
      viewer.renderer?.setClearColor(color, 1)
      viewer.Render?.()
    } catch (err) {
      console.warn('CadPreview bg:', err)
    }
  }, [isDarkBg, isReady])

  // ── Fullscreen change listener ─────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  // ── Sync zoom % on native scroll / touchpad zoom ───────────────────────────
  useEffect(() => {
    const el = containerRef.current
    if (!el || !isReady) return

    let rafId = null

    const syncZoom = () => {
      const viewer = viewerRef.current
      const b = boundsRef.current
      if (!viewer || !b) return
      try {
        const cam = viewer.GetCamera?.()
        if (!cam) return
        const currentWidth = (cam.right - cam.left) / cam.zoom
        const fitWidth = b.maxX - b.minX || 1
        dispatch({ type: 'SET_ZOOM', payload: Math.round((fitWidth / currentWidth) * 100) })
      } catch (_) { }
    }

    const onWheel = () => {
      // Read camera state on the next frame, after DxfViewer has processed the event
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(syncZoom)
    }

    el.addEventListener('wheel', onWheel, { passive: true })
    return () => {
      el.removeEventListener('wheel', onWheel)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isReady])

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return
      if (e.key === '+' || e.key === '=') handleZoomIn()
      else if (e.key === '-') handleZoomOut()
      else if (e.key === 'f' || e.key === 'F') handleFitView()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── Zoom ──────────────────────────────────────────────────────────────────
  const applyZoom = useCallback((factor) => {
    const viewer = viewerRef.current
    if (!viewer) return
    try {
      const cam = viewer.GetCamera?.()
      if (!cam) return
      const currentWidth = (cam.right - cam.left) / cam.zoom
      const newWidth = Math.max(0.001, currentWidth / factor)
      viewer.SetView({ x: cam.position.x, y: cam.position.y }, newWidth)
      viewer.Render?.()
      const b = boundsRef.current
      if (b) {
        const fitWidth = b.maxX - b.minX || 1
        dispatch({ type: 'SET_ZOOM', payload: Math.round((fitWidth / newWidth) * 100) })
      }
    } catch (err) {
      console.warn('CadPreview zoom:', err)
    }
  }, [])

  const handleZoomIn = useCallback(() => applyZoom(1.25), [applyZoom])
  const handleZoomOut = useCallback(() => applyZoom(1 / 1.25), [applyZoom])

  const handleFitView = useCallback(() => {
    if (!viewerRef.current) return
    try {
      const b = boundsRef.current ?? viewerRef.current.GetBounds?.()
      if (b) {
        viewerRef.current.FitView(b.minX, b.maxX, b.minY, b.maxY)
        viewerRef.current.Render?.()
        dispatch({ type: 'SET_ZOOM', payload: 100 })
      }
    } catch (err) {
      console.warn('CadPreview fit:', err)
    }
  }, [])

  // ── Layer visibility ───────────────────────────────────────────────────────
  const setLayerVisible = useCallback((name, newVisible) => {
    const viewer = viewerRef.current
    if (!viewer) return
    if (typeof viewer.ShowLayer === 'function') {
      viewer.ShowLayer(name, newVisible)
    } else if (typeof viewer.SetLayerVisibility === 'function') {
      viewer.SetLayerVisibility(name, newVisible)
    } else if (typeof viewer.setLayerVisibility === 'function') {
      viewer.setLayerVisibility(name, newVisible)
    }
    viewer.Render?.()
  }, [])

  const toggleLayer = useCallback((name) => {
    setLayers((prev) =>
      prev.map((layer) => {
        if (layer.name !== name) return layer
        const newVisible = !layer.visible
        setLayerVisible(name, newVisible)
        return { ...layer, visible: newVisible }
      }),
    )
    setLayerVisibility((prev) => ({ ...prev, [name]: !prev[name] }))
  }, [setLayerVisible])

  // ── Toggle ALL layers ──────────────────────────────────────────────────────
  const toggleAllLayers = useCallback(() => {
    const nextVisible = !allLayersVisible
    setLayers((prev) =>
      prev.map((layer) => {
        setLayerVisible(layer.name, nextVisible)
        return { ...layer, visible: nextVisible }
      }),
    )
    setLayerVisibility((prev) => {
      const next = {}
      Object.keys(prev).forEach((k) => { next[k] = nextVisible })
      return next
    })
  }, [allLayersVisible, setLayerVisible])


  // ── Fullscreen ─────────────────────────────────────────────────────────────
  const handleFullscreen = useCallback(() => {
    const el = paperRef.current
    if (!el) return
    document.fullscreenElement ? document.exitFullscreen() : el.requestFullscreen?.()
  }, [])

  // ── Retry ──────────────────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    retryCount.current += 1
    forceUpdate()
  }, [forceUpdate])

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        ref: paperRef,
        sx: { height: '90vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ═══ Toolbar ═══════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.75,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 0.5,
        }}
      >
        {/* Left: title + file name + zoom badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: '#444', whiteSpace: 'nowrap' }}
          >
            CAD Preview
          </Typography>
          {fileName && (
            <Typography
              variant="caption"
              sx={{
                color: '#999',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
              }}
            >
              {fileName}
            </Typography>
          )}
          {isReady && vState.zoom != null && (
            <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, backgroundColor: '#ebebeb' }}>
              <Typography
                sx={{ fontSize: 11, color: '#555', fontWeight: 600, fontFamily: 'monospace' }}
              >
                {vState.zoom}%
              </Typography>
            </Box>
          )}
        </Box>

        {/* Centre: tool groups */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
          {/* Zoom + fit */}
          <ToolBtn title="Zoom In (+)" onClick={handleZoomIn} disabled={!isReady}>
            <ZoomInIcon fontSize="small" />
          </ToolBtn>
          <ToolBtn title="Zoom Out (-)" onClick={handleZoomOut} disabled={!isReady}>
            <ZoomOutIcon fontSize="small" />
          </ToolBtn>
          <ToolBtn title="Fit View (F)" onClick={handleFitView} disabled={!isReady}>
            <FitScreenIcon fontSize="small" />
          </ToolBtn>

          <ToolDivider />

          {/* Background toggle */}
          <ToolBtn
            title={isDarkBg ? 'Switch to Light Background' : 'Switch to Dark Background'}
            onClick={() => setIsDarkBg((v) => !v)}
            disabled={!isReady}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.5, color: 'inherit' }}>
              {isDarkBg ? '☀' : '🌙'}
            </Typography>
          </ToolBtn>

          <ToolDivider />

          {/* Layers panel toggle */}
          <ToolBtn
            title="Layers Panel"
            onClick={() => setShowLayerPanel((v) => !v)}
            disabled={!isReady}
            active={showLayerPanel}
          >
            <LayersIcon fontSize="small" />
          </ToolBtn>

          <ToolDivider />

          {/* Fullscreen */}
          <ToolBtn
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            onClick={handleFullscreen}
            disabled={!isReady}
          >
            {isFullscreen ? (
              <FullscreenExitIcon fontSize="small" />
            ) : (
              <FullscreenIcon fontSize="small" />
            )}
          </ToolBtn>
        </Box>

        {/* Right: close */}
        <ToolBtn title="Close" onClick={onClose}>
          <CancelOutlined fontSize="small" />
        </ToolBtn>
      </Box>

      {/* ═══ Body ══════════════════════════════════════════════════════════ */}
      <DialogContent
        sx={{ flex: 1, p: 0, overflow: 'hidden', position: 'relative', display: 'flex' }}
      >
        {/* ── Layer panel (collapsible left sidebar) ── */}
        <Fade in={showLayerPanel && isReady}>
          <Box
            sx={{
              width: 230,
              flexShrink: 0,
              borderRight: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              overflowY: 'auto',
              py: 1,
              display: showLayerPanel && isReady ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            {/* Panel header */}
            <Box
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  color: '#555',
                  fontSize: 11,
                }}
              >
                Layers
              </Typography>
              <Typography variant="caption" sx={{ color: '#bbb', fontFamily: 'monospace' }}>
                {layers.length}
              </Typography>
            </Box>

            {layers.length === 0 ? (
              <Typography sx={{ px: 2, color: '#bbb', fontSize: 12 }}>
                No layer data available
              </Typography>
            ) : (
              <>
                {/* ── All Layers toggle row ── */}
                <Box
                  onClick={toggleAllLayers}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.6,
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    mb: 0.5,
                    backgroundColor: 'rgba(124,131,253,0.04)',
                    '&:hover': { backgroundColor: 'rgba(124,131,253,0.08)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: 'linear-gradient(135deg, #7c83fd 50%, #aaa 50%)',
                      mr: 1,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      fontFamily: 'monospace',
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: '#7c83fd',
                    }}
                  >
                    All Layers
                  </Typography>
                  <Switch
                    size="small"
                    checked={allLayersVisible}
                    onChange={() => { }}
                    sx={{ ml: 0.5, pointerEvents: 'none', flexShrink: 0 }}
                  />
                </Box>

                {/* ── Individual layer rows ── */}
                {layers.map((layer) => {
                  const name = layer.name
                  const visible = layerVisibility[name] ?? true
                  const color = layer.color
                  return (
                    <Box
                      key={name}
                      onClick={() => toggleLayer(name)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                        py: 0.6,
                        cursor: 'pointer',
                        opacity: visible ? 1 : 0.4,
                        transition: 'opacity 0.2s',
                        '&:hover': { backgroundColor: '#f7f7f7' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: color,
                          mr: 1,
                          border: '1px solid rgba(0,0,0,0.1)',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                          fontSize: 11.5,
                          color: '#444',
                        }}
                      >
                        {name}
                      </Typography>
                      <Switch
                        size="small"
                        checked={visible}
                        onChange={() => { }}
                        sx={{ ml: 0.5, pointerEvents: 'none', flexShrink: 0 }}
                      />
                    </Box>
                  )
                })}
              </>
            )}
          </Box>
        </Fade>

        {/* ── Canvas area ── */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: isDarkBg ? '#1a1a2e' : '#f0f0f0',
            transition: 'background-color 0.3s',
          }}
        >
          <CadErrorBoundary onClose={onClose}>
            {/* Loading overlay */}
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  backgroundColor: '#1a1a2e',
                }}
              >
                <CircularProgress size={40} sx={{ color: '#7c83fd' }} />
                <Typography sx={{ mt: 2, color: '#aaa', fontSize: 14 }}>
                  Loading CAD drawing…
                </Typography>
                {loadingPercentage > 0 && (
                  <Box sx={{ width: '200px', mt: 2, textAlign: 'center' }}>
                    <LinearProgress
                      variant="determinate"
                      value={loadingPercentage}
                      sx={{ height: 6, borderRadius: 3, mb: 1, backgroundColor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { backgroundColor: '#7c83fd' } }}
                    />
                    <Typography sx={{ color: '#aaa', fontSize: 12 }}>{loadingPercentage}%</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Error overlay */}
            {hasError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#1a1a2e',
                }}
              >
                <BrokenImageIcon sx={{ fontSize: 56, color: '#555', mb: 2 }} />
                <Typography
                  sx={{ color: '#888', fontSize: 14, textAlign: 'center', maxWidth: 400, px: 2 }}
                >
                  {vState.error}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplayIcon />}
                    sx={{ color: '#7c83fd', borderColor: '#7c83fd' }}
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ color: '#888', borderColor: '#555' }}
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            )}

            {/* Interaction hint bar */}
            <Fade in={isReady} timeout={800}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 5,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0,0,0,0.45)',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography sx={{ color: '#aaa', fontSize: 11 }}>
                  Scroll to zoom · Click &amp; drag to pan · F to fit
                </Typography>
              </Box>
            </Fade>

            {/* WebGL container */}
            <Box
              ref={containerRef}
              sx={{
                width: '100%',
                height: '100%',
                visibility: isLoading || hasError ? 'hidden' : 'visible',
              }}
            />
          </CadErrorBoundary>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default CadPreview